/** `[upload_id "the words"]` rendered as a named, clickable source.
 *
 * Shared by every surface that shows agent prose - the state document and the
 * conversation transcript - because an operator should not have to learn that a
 * citation is checkable in one place and raw text in another.
 *
 * Two things this fixes over doing it per component:
 *
 * - The href carries the citation, so any number of independently-rendered
 *   bodies (a transcript's messages, a document's folded sections) share one
 *   modal without coordinating indices between them.
 * - The link's TEXT is the id, and the renderer swaps it for the document's
 *   name. `ORNicun-A-YTc7jqgmh9Jg` tells a reader nothing about whether the
 *   claim rests on the lease or on a draft somebody superseded.
 */
import { useMemo, useState, type ReactNode } from "react";
import type { Components } from "react-markdown";
import { Link } from "react-router-dom";

import { useRunsDescribe } from "../api/hooks/useRunsDescribe";
import { useUploadsDescribe } from "../api/hooks/useUploadsDescribe";
import { runLinkPath } from "../routes/learningAgentPaths";
import { runName, useRunNameMap } from "../api/hooks/useRunNames";
import { SlackDayModal } from "./SlackDayModal";
import { SourceTextModal } from "./SourceTextModal";
import styles from "./CitedBody.module.css";
import { UPLOAD_ID } from "../utils/uploadId";

// An upload id (UPLOAD_ID) in square brackets, optionally credited
// ("via grid:") and optionally carrying the quoted words the claim rests on.
// Deliberately NOT a loose `\[[^\]]+\]` - agent prose is full of other bracketed
// tokens (Gmail message ids, rule ids, run refs) and turning those into dead
// links would teach the reader to ignore all of them.
const CITATION = new RegExp(
  String.raw`\[(?:via\s+([a-z][a-z0-9_]*)\s*:\s*)?(${UPLOAD_ID})(?:\s+["“](.{1,1000}?)["”])?\]`,
  "gi",
);

// `[run <id> "words"]`: an operator's words in another run, as read_ledger._RUN_RE
// accepts it. Links to the run's own page, which sends each kind where it lives.
const RUN_CITATION = /\[run\s+([0-9a-f]{32})\s+["“](.{1,1000}?)["”]\]/gi;
const RUN_SCHEME = "run:";

// Code spans and fences are left as written: a citation shown as code is an example.
const CODE = /(```[\s\S]*?```|`[^`\n]*`)/;

const CITE_SCHEME = "cite:";

interface Cite {
  id: string;
  quote: string;
  via: string;
  // The sentence leans on the quote for its meaning ("caps export at [id "1,260 kW"]"),
  // so the quote is shown, not just the source's name.
  inline: boolean;
}

// Whether the sentence before a citation needs its quote to be complete. A heuristic
// that must agree with agents/tools/state/hanging_citations.py, which sends such
// answers back; this shows the quote for any that remain.
const DANGLING_WORDS = new Set(
  `a an the and or as at by for from in into of on than that to under until with
  within whether is are was were be been says said reads read states stated wrote
  writes gives gave shows showed describes described names named records recorded
  quotes quoted specifies specified sets set asked asks told tells replied replies
  confirmed confirms notes noted lists listed requires required calls called`.split(
    /\s+/,
  ),
);
const REPORTING_WORDS = new Set(
  `says said wrote writes told tells asked asks replied replies confirmed confirms
  stated states noted notes added adds warned warns explained explains`.split(
    /\s+/,
  ),
);
const AUXILIARIES = new Set(
  `is are was were be been has have had will would can could must may might should
  shall do does did needs need`.split(/\s+/),
);
const SHORT_CLAUSE_WORDS = 3;
const ADDRESSING_WORDS = new Set(["told", "tells", "asked", "asks"]);
const TRAILING_DATE = /\s+on\s+\d{1,2}\s+[a-z]{3,9}(?:\s+\d{4})?\s*$/i;
// read_ledger.BRACKET_RE: every bracketed span, citation or not.
const BRACKET = /\[[^[\]]{0,1100}\]/g;
const wordsOf = (text: string): string[] =>
  text.toLowerCase().match(/[a-z0-9][a-z0-9'&-]*/g) ?? [];

export const hangsOnQuote = (before: string): boolean => {
  if (/:\s*$/.test(before)) return true;
  // A second source for the same claim ("[...] and [...]") hangs on nothing.
  if (/\]\s*(?:,|and|or)?\s*$/.test(before)) return false;
  const sentence = before
    .replace(BRACKET, " ")
    .split(/[.!?]\s+|\n/)
    .pop()!;
  const plain = wordsOf(sentence);
  if (plain.length === 0) return false;
  if (DANGLING_WORDS.has(plain[plain.length - 1])) return true;
  // The date says when, not what: dropped for the reporting rules only.
  const words = wordsOf(sentence.replace(TRAILING_DATE, ""));
  let last = -1;
  words.forEach((w, i) => {
    if (REPORTING_WORDS.has(w)) last = i;
  });
  if (last < 0) return false;
  const after = words.slice(last + 1);
  if (after[0] === "that" || after[0] === "whether") {
    const clause = after.slice(1);
    return (
      clause.length <= SHORT_CLAUSE_WORDS &&
      !clause.some((w) => AUXILIARIES.has(w))
    );
  }
  return ADDRESSING_WORDS.has(words[last]) && after.length === 1;
};

// encodeURIComponent escapes ":" and "%" (so the split below is safe) but NOT
// parentheses, and a markdown link destination may only carry balanced ones. An
// agent quoting "see clause 3) of the lease" or "the Landlord (as defined in" is
// routine, and either one silently truncates the link or spills the raw markdown
// into the sentence - the symptom this whole component exists to remove.
const part = (raw: string): string =>
  encodeURIComponent(raw).replace(/\(/g, "%28").replace(/\)/g, "%29");

const encode = (c: Cite): string =>
  `#${CITE_SCHEME}${part(c.id)}:${part(c.via)}:${part(c.quote)}:${c.inline ? "1" : ""}`;

/** The href that `components.a` renders as a link to one upload, for a source
 *  recorded as a field rather than cited in prose. */
export const sourceHref = (uploadId: string): string =>
  encode({ id: uploadId, via: "", quote: "", inline: false });

const decode = (href: string | undefined): Cite | undefined => {
  if (href === undefined || !href.startsWith(`#${CITE_SCHEME}`))
    return undefined;
  const [id, via, quote, inline] = href
    .slice(CITE_SCHEME.length + 1)
    .split(":");
  if (!id) return undefined;
  return {
    id: decodeURIComponent(id),
    via: decodeURIComponent(via ?? ""),
    quote: decodeURIComponent(quote ?? ""),
    inline: inline === "1",
  };
};

/** Every upload id cited anywhere in `bodies`. */
export const citedUploadIds = (bodies: string[]): string[] => {
  const ids: string[] = [];
  for (const body of bodies) {
    for (const m of body.matchAll(CITATION)) ids.push(m[2]);
  }
  return ids;
};

/** Every run cited anywhere in `bodies`, so each link reads as the conversation's name. */
const citedRunIds = (bodies: string[]): string[] =>
  bodies.flatMap((body) =>
    [...body.matchAll(RUN_CITATION)].map((m) => m[1].toLowerCase()),
  );

/** Rewrite one body's citations into links this hook's `components` render. */
const withCitationLinks = (body: string): string =>
  body
    .split(CODE)
    .map((chunk, i) =>
      i % 2 === 1
        ? chunk
        : chunk
            .replace(
              RUN_CITATION,
              (
                _match,
                runId: string,
                quote: string,
                offset: number,
                whole: string,
              ) =>
                `[run](#${RUN_SCHEME}${runId.toLowerCase()}:${part(quote)}:${
                  hangsOnQuote(whole.slice(0, offset)) ? "1" : ""
                })`,
            )
            .replace(
              CITATION,
              (
                _match,
                via: string | undefined,
                id: string,
                quote: string | undefined,
                offset: number,
                whole: string,
              ) =>
                `[${id}](${encode({
                  id,
                  via: via ?? "",
                  quote: quote ?? "",
                  inline:
                    Boolean(quote) && hangsOnQuote(whole.slice(0, offset)),
                })})`,
            ),
    )
    .join("");

export interface Citations {
  /** Apply to each body before handing it to `<Markdown>`. */
  transform: (body: string) => string;
  /** Pass to every `<Markdown>` rendering a transformed body. */
  components: Components;
  /** Render once, anywhere inside the same tree. */
  modal: ReactNode;
}

/** `bodies` is everything that will be rendered, so the names resolve in one
 *  request rather than one per citation. */
export const useCitations = (bodies: string[]): Citations => {
  const [open, setOpen] = useState<Cite | null>(null);
  const ids = useMemo(() => citedUploadIds(bodies), [bodies]);
  const { byId: described, resolved } = useUploadsDescribe(ids);
  // A page that already read its runs names them through context; only the rest are
  // looked up, in one request.
  const runNames = useRunNameMap();
  const runIds = useMemo(
    () => citedRunIds(bodies).filter((id) => !runNames.has(id)),
    [bodies, runNames],
  );
  const { byId: runs, resolved: runsResolved } = useRunsDescribe(runIds);
  const cited = open === null ? undefined : described.get(open.id);

  const components = useMemo<Components>(
    () => ({
      a: ({ href, children }) => {
        if (href?.startsWith(`#${RUN_SCHEME}`)) {
          const [runId, rawQuote, inline] = href
            .slice(RUN_SCHEME.length + 1)
            .split(":");
          const quote = decodeURIComponent(rawQuote ?? "");
          const known = runNames.get(runId) ?? runs.get(runId);
          const name =
            typeof known === "string"
              ? known
              : known
                ? runName(known)
                : runsResolved
                  ? "conversation not available"
                  : "conversation";
          const link = (
            <Link
              to={runLinkPath(runId)}
              className={styles.citation}
              title={`Open the conversation where this was said: "${quote}"`}
            >
              {/* Absent once resolved: no such run, or someone else's private one. */}
              [{name}]
            </Link>
          );
          // As for a source: a sentence that leans on the words shows them.
          return inline === "1" ? (
            <>
              {`“${quote}” `}
              {link}
            </>
          ) : (
            link
          );
        }
        const cite = decode(href);
        if (cite === undefined) return <a href={href}>{children}</a>;
        const known = described.get(cite.id);
        // The name once we have it, the id until then - never a spinner in the
        // middle of a sentence.
        const label = `${cite.via ? `via ${cite.via}: ` : ""}${
          known ? known.display_name : `${cite.id.slice(0, 8)}…`
        }`;
        // A link, not a button: it wraps with the sentence and copies as text.
        const link = (
          <a
            href={href}
            className={styles.citation}
            title={
              cite.quote
                ? `Open the source at: "${cite.quote}"`
                : "Open the source this claim cites"
            }
            onClick={(event) => {
              event.preventDefault();
              setOpen(cite);
            }}
          >
            [{label}]
          </a>
        );
        return cite.inline ? (
          <>
            {`“${cite.quote}” `}
            {link}
          </>
        ) : (
          link
        );
      },
    }),
    [described, runNames, runs, runsResolved],
  );

  const modal = (
    <>
      {open !== null && !resolved && (
        <p className={styles.pending}>Finding the cited source…</p>
      )}
      {/* A citation naming an upload the platform does not have is a finding in
          its own right - a fabricated id, or a document since deleted - so say
          so plainly rather than opening an empty viewer. */}
      {open !== null && resolved && cited === undefined && (
        <p className={styles.missing}>
          No file with id <code>{open.id}</code>, so the claim citing it cannot
          be checked here.{" "}
          <button
            type="button"
            className={styles.citation}
            onClick={() => setOpen(null)}
          >
            Dismiss
          </button>
        </p>
      )}
      {open !== null && cited !== undefined && cited.kind === "slack_day" && (
        <SlackDayModal
          globalDataId={cited.upload_id}
          quote={open.quote || null}
          onClose={() => setOpen(null)}
        />
      )}
      {open !== null && cited !== undefined && cited.kind !== "slack_day" && (
        <SourceTextModal
          {...(cited.source === "global"
            ? { globalDataId: cited.upload_id }
            : { uploadId: cited.upload_id })}
          // Same rule the Files tab uses: text tab only when a text layer was
          // persisted. A vision-read drawing opens on the document tab instead.
          hasSourceText={(cited.extracted_text_chars ?? 0) > 0}
          // The claim's own quote, so the viewer opens at the passage it rests on.
          evidence={open.quote || null}
          cited
          sourceLabel={cited.display_name}
          contentType={cited.content_type}
          driveFileId={cited.drive_file_id}
          drivePath={cited.drive_path}
          onClose={() => setOpen(null)}
        />
      )}
    </>
  );

  return { transform: withCitationLinks, components, modal };
};
